import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { CircularProgress, Tooltip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import DirectionsRunIcon from '@material-ui/icons/DirectionsRun';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { format } from 'date-fns';
import classnames from 'classnames';
import { AgGrid } from '../../components/AgGrid';
import { getAutomationById } from '../../services';
import ErrorMessage from '../../components/Error/ErrorMessage';
import { useParams } from 'react-router-dom';

const RunStatusCellRenderer = (props) => {
  const value = props.value?.toLowerCase();

  if (!value) {
    return <span>-</span>;
  }

  if (value === 'running') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Running" placement="top">
          <DirectionsRunIcon style={{ color: '#ff9800', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }

  if (value === 'success') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Success" placement="top">
          <CheckCircleIcon style={{ color: '#4caf50', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }

  if (value === 'dryrun') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Dry Run (non-prod - execution skipped)" placement="top">
          <VisibilityIcon style={{ color: '#9c27b0', fontSize: 24 }} />
        </Tooltip>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Tooltip title="Failure" placement="top">
        <ErrorIcon style={{ color: '#f44336', fontSize: 24 }} />
      </Tooltip>
    </div>
  );
};

const CreatedOnCellRenderer = (props) => {
  const value = props.value;

  if (!value) {
    return <span>-</span>;
  }

  try {
    const date = new Date(value);
    const formattedDate = format(date, 'MM/dd/yyyy hh:mm:ss a');

    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <span>{formattedDate}</span>
      </div>
    );
  } catch (error) {
    console.error('Error parsing date:', error);
    return <span>{value}</span>;
  }
};

const styles = theme => ({});

export const AutomationRunsGrid = withStyles(styles)(props => {
  const [automation, setAutomation] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const { automationId } = useParams();

  const fetchAutomationById = async () => {
    try {
      const automationsData = await getAutomationById(automationId);
      setAutomation(automationsData);
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomationById();
  }, []);

  const columnDefs = [
    { headerName: 'Run ID', field: 'automationRunId', hide: true },
    { headerName: 'Identifier', field: 'identifier', width: 320, resizable: true },
    {
      headerName: 'Status',
      field: 'status',
      width: 125,
      resizable: true,
      cellRenderer: 'runStatusCellRenderer',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['Running', 'Success', 'Failure', 'DryRun'],
        cellRenderer: 'runStatusCellRenderer'
      }
    },
    {
      headerName: 'Created On',
      field: 'createdOn',
      width: 200,
      type: 'dateColumn',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) {
            return -1;
          } else if (cellDate > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        }
      },
      cellRenderer: 'createdOnCellRenderer',
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      sort: 'desc',
      valueGetter: (params) => {
        return params.data?.createdOn ? new Date(params.data.createdOn) : null;
      }
    }
  ];

  const gridOptions = {
    suppressColumnVirtualisation: true,
    defaultColDef: {
      resizable: true,
      suppressAutoSize: false,
      flex: 1
    },
    frameworkComponents: {
      runStatusCellRenderer: RunStatusCellRenderer,
      createdOnCellRenderer: CreatedOnCellRenderer
    },
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [10, 25, 50, 100],
    suppressPaginationPanel: false
  };

  if (isLoading) {
    return (
      <div className={classnames('flex-1', 'card', 'border-left-green')} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }
  if (isError) {
    return <ErrorMessage message="There was an error fetching automation runs." />;
  }

  return (
    <div className={classnames('flex-1', 'card', 'border-left-green')}>
      <h3 style={{ margin: 0 }}>{automation.friendlyJobName} Runs</h3>
      <AgGrid
        gridData={automation.runs}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        redirectOnClick={true}
        editOnClick={false}
        handleCellClick={evt => {
          const automationRunId = evt.data.automationRunId;
          if (automationRunId) {
            window.location = `${window.location.href}/${automationRunId}`;
          }
        }}
        rowClassRules={{}}
      />
    </div>
  );
});
